/* Certificates: the tally, the barrage, the wall, and the full size viewer (sections/mc-certificates.liquid).
   Scroll decides which certificate is up (the bottom of the screen against the runway's stretches); every move plays
   on its own quick timing, so it is punchy at any scroll speed. A fast scroll skips straight to the current one, and
   scrolling back runs it in reverse. The wall is the real content; the flying copies are decoration (aria-hidden). */
(() => {
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const easeIn = (t) => t * t;
  const smooth = (a, b, v) => { const t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
  const loglerp = (a, b, t) => Math.exp(lerp(Math.log(Math.max(a, 0.01)), Math.log(Math.max(b, 0.01)), t));
  // Restart a class-driven animation without forcing a layout: off now, on in the next frame
  const replay = (el, c, ms) => {
    el.classList.remove(c);
    cancelAnimationFrame(el.mcReplay);
    clearTimeout(el.mcReplayT);
    el.mcReplay = requestAnimationFrame(() => {
      el.classList.add(c);
      el.mcReplayT = setTimeout(() => el.classList.remove(c), ms);
    });
  };

  // Timings (ms): a launch and landing, longer for hero moments; slammed onto the deck; the reverse moves; the wall
  const T = { launch: 500, hero: 520, slam: 300, skip: 320, retreat: 360, back: 420, explode: 820, stagger: 22, gather: 420 };
  // Keeping up with the scroll: up to SKIP certificates behind, they fire in order, faster the further behind (a burst
  // of rapid hits); further behind than that (a fast scroll), it skips straight to the current one
  const SKIP = 4, PACE = [1, 1, 0.3, 0.24, 0.2];
  // A hero moment (Honors, AI, Specialization) always plays in full: its launch at full length, then the queue holds
  // until its impact and effect have played (HOLD from launch); waiting regulars then catch up in rapid fire. Only a
  // fast scroll (FAST screens a second) skips ahead, so at a normal pace no hero is ever skipped or cut short.
  const HOLD = 950, FAST = 1.8;
  const AR = 1.2945; // every certificate shows in the same frame shape (aspect-ratio in the section's CSS)

  // Shockwave rings and a particle burst, restarted wherever a hit lands
  function makeFx(nP) {
    const fx = document.createElement('span');
    fx.className = 'mc-fx';
    fx.setAttribute('aria-hidden', 'true');
    const rings = [0, 1, 2].map(() => { const r = document.createElement('i'); r.className = 'mc-fx__ring'; fx.append(r); return r; });
    for (let k = 0; k < nP; k++) {
      const p = document.createElement('i');
      p.className = k % 2 ? 'mc-fx__p mc-fx__p--b' : 'mc-fx__p';
      const jit = (s) => { const v = Math.sin(k * s) * 43758.5453; return v - Math.floor(v); };
      p.style.setProperty('--a', ((k / nP) * 360 + jit(12.9898) * 14).toFixed(1) + 'deg');
      p.style.setProperty('--j', (0.55 + jit(78.233) * 0.6).toFixed(2));
      fx.append(p);
    }
    const looks = {
      hit: [['aqua', 1.25, 0], ['lime', 0.9, 90]],
      honors: [['aqua', 1.25, 0], ['gold', 1.7, 80], ['gold', 1.05, 150]],
      ai: [['lime', 1.3, 0], ['lime', 0.85, 80]],
      spec: [['aqua', 1.5, 0], ['white', 1.1, 70], ['aqua', 2.0, 140]],
      blast: [['white', 1.2, 0], ['aqua', 2.2, 60], ['lime', 3.0, 160]],
      tally: [['lime', 1.3, 0], ['aqua', 2.0, 110]],
    };
    const col = { aqua: 'var(--mc-aqua)', lime: 'var(--mc-lime)', gold: '#f2c862', white: '#e9f6ff' };
    fx.go = (x, y, size, kind) => {
      const look = looks[kind] || looks.hit;
      rings.forEach((r, k) => {
        const l = look[k];
        r.style.display = l ? '' : 'none';
        if (!l) return;
        r.style.setProperty('--r', Math.round(size * l[1]) + 'px');
        r.style.setProperty('--c', col[l[0]]);
        r.style.setProperty('--d', l[2] + 'ms');
      });
      fx.dataset.kind = kind;
      fx.style.setProperty('--d1', Math.round(size * 0.62) + 'px');
      fx.style.setProperty('--d0', Math.round(size * 0.12) + 'px');
      fx.style.transform = `translate(${Math.round(x)}px,${Math.round(y)}px)`;
      replay(fx, 'is-go', 1300);
    };
    return fx;
  }

  // A subtle camera kick: the sky and the flight layer jolt and settle
  function kickCamera(els, big) {
    const c = big ? 'mc-kick--big' : 'mc-kick';
    els.forEach((el) => {
      if (!el) return;
      el.classList.remove('mc-kick', 'mc-kick--big');
      replay(el, c, 450);
    });
  }
  const sky = () => document.querySelector('canvas.mc-journey');

  function viewer(section) {
    const dlg = section.querySelector('[data-mc-cert-view]');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    const img = dlg.querySelector('.mc-cert-view__img');
    const name = dlg.querySelector('.mc-cert-view__name');
    const verify = dlg.querySelector('[data-mc-cert-verify]');
    section.addEventListener('click', (e) => {
      const a = e.target.closest('[data-mc-cert-open]');
      if (!a || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      const li = a.closest('.mc-cert');
      const src = a.querySelector('img');
      img.removeAttribute('src');
      img.srcset = src.srcset;
      img.sizes = 'min(1400px, 100vw)';
      img.src = src.currentSrc || src.src;
      img.setAttribute('width', src.getAttribute('width'));
      img.setAttribute('height', src.getAttribute('height'));
      img.alt = src.alt;
      name.textContent = li.querySelector('.mc-cert__name').textContent;
      const v = li.querySelector('.mc-cert__verify');
      verify.hidden = !v;
      if (v) verify.href = v.href;
      dlg.showModal();
    });
    dlg.addEventListener('click', (e) => {
      if (e.target.closest('[data-mc-cert-close]') || !e.target.closest('.mc-cert-view__img, .mc-cert-view__cap')) dlg.close();
    });
  }

  // The count: once the whole head is on screen together, it races up and slams, a shockwave rolls out, the label and
  // the issuers' logos flash in. Again on each fresh arrival from above. Tells the barrage when it has landed.
  function tally(section, onChange) {
    const head = section.querySelector('[data-mc-certs-head]');
    const box = section.querySelector('[data-mc-tally]');
    const num = section.querySelector('[data-mc-count]');
    const ctl = { done: true };
    if (!head || !box || !num || !('IntersectionObserver' in window)) { section.classList.add('is-tallied'); return ctl; }
    ctl.done = false;
    const to = +num.dataset.to || +num.textContent || 0;
    const fx = makeFx(22);
    box.append(fx);
    let raf = 0, timer = 0;
    const run = () => {
      section.classList.remove('is-tallied');
      section.classList.add('is-tallying');
      const t0 = performance.now(), dur = 520;
      const tick = (now) => {
        const t = clamp((now - t0) / dur, 0, 1);
        num.textContent = Math.round(easeOut(t) * to);
        if (t < 1) { raf = requestAnimationFrame(tick); return; }
        section.classList.remove('is-tallying');
        section.classList.add('is-tallied');
        const r = num.getBoundingClientRect(), b = box.getBoundingClientRect();
        fx.go(r.left - b.left + r.width / 2, r.top - b.top + r.height / 2, Math.max(r.width, r.height) * 1.6, 'tally');
        kickCamera([sky()], false);
        ctl.done = true;
        onChange();
      };
      raf = requestAnimationFrame(tick);
    };
    const hd = document.querySelector('.mc-header');
    const top = Math.round((hd && hd.getBoundingClientRect().height) || 68);
    const io = new IntersectionObserver((es) => {
      for (const e of es) {
        // all of it on screen (or, for a head taller than the screen, all the screen it can fill), within one threshold step
        const bh = e.boundingClientRect.height;
        const whole = e.intersectionRect.height >= Math.min(bh, e.rootBounds ? e.rootBounds.height : innerHeight) - Math.max(2, bh * 0.03);
        if (e.isIntersecting && whole) {
          if (!ctl.done && !section.classList.contains('is-tallying')) { clearTimeout(timer); timer = setTimeout(run, 40); }
        } else if (!e.isIntersecting && e.boundingClientRect.top > 0) {
          // gone below the screen (the reader went back up): it slams in again next time
          cancelAnimationFrame(raf); clearTimeout(timer);
          section.classList.remove('is-tallied', 'is-tallying');
          num.textContent = to;
          if (ctl.done) { ctl.done = false; onChange(); }
        }
      }
    }, { rootMargin: `-${top}px 0px 0px 0px`, threshold: Array.from({ length: 51 }, (_, k) => k / 50) });
    io.observe(head);
    return ctl;
  }

  function sequence(section) {
    const run = section.querySelector('.mc-certs__run');
    const head = section.querySelector('[data-mc-certs-head]');
    const segs = [...run.querySelectorAll('.mc-certs__seg:not(.mc-certs__seg--lead)')];
    const items = [...section.querySelectorAll('.mc-cert')];
    const marks = [...section.querySelectorAll('.mc-certs__beacons [data-mc-ach]')];
    const parts = section.querySelector('[data-mc-cert-parts]');
    const N = items.length;
    const kinds = items.map((li) => li.dataset.kind || '');
    let layer = null, fx = null, flyers = [];
    let G = null, cur = -1, raf = 0, dimOff = 0, pace = 1, wasDim = false;
    const ctl = section.classList.contains('is-tally-armed') ? tally(section, () => kick()) : { done: true };

    function build() {
      layer = document.createElement('div');
      layer.className = 'mc-flyers';
      layer.setAttribute('aria-hidden', 'true');
      layer.hidden = true;
      const dim = document.createElement('span');
      dim.className = 'mc-flyers__dim';
      layer.append(dim);
      flyers = items.map((li, i) => {
        const src = li.querySelector('.mc-cert__img');
        const el = document.createElement('div');
        el.className = 'mc-fly';
        if (kinds[i]) el.dataset.kind = kinds[i];
        const card = document.createElement('div');
        card.className = 'mc-fly__card';
        const img = document.createElement('img');
        img.alt = '';
        img.decoding = 'async';
        img.setAttribute('width', src.getAttribute('width') || 2000);
        img.setAttribute('height', src.getAttribute('height') || 1545);
        card.append(img);
        const holo = document.createElement('span');
        holo.className = 'mc-fly__holo';
        card.append(holo);
        if (parts) {
          card.append(parts.content.querySelector('.mc-fly__chip').cloneNode(true));
          const extra = kinds[i] && parts.content.querySelector(`[data-for="${kinds[i]}"]`);
          if (extra) card.append(extra.cloneNode(true));
        }
        if (kinds[i] === 'ai') {
          const ch = document.createElement('span');
          ch.className = 'mc-fly__charge';
          card.append(ch);
        }
        const cap = document.createElement('p');
        cap.className = 'mc-fly__cap';
        const line = li.querySelector('.mc-cert__line');
        if (line) cap.append(line.cloneNode(true));
        const nm = document.createElement('span');
        nm.className = 'mc-cert__name';
        nm.textContent = li.querySelector('.mc-cert__name').textContent;
        cap.append(nm);
        el.append(card, cap);
        layer.append(el);
        return { i, li, el, card, img, src, holo, h: 0, k: '', op: -1, ho: -1, st: 'away', pose: null, legs: [], leg: null, cls: {}, loaded: false };
      });
      fx = makeFx(28);
      layer.append(fx);
      document.body.append(layer);
      layout();
      // land straight in the right state (no fly-ins for where the reader already is)
      const t = target();
      cur = t;
      flyers.forEach((f) => { f.st = want(f.i, t); f.pose = null; toggle(f.li, 'is-placed', t >= N); });
      for (let k = 0; k < Math.min(N, Math.max(0, t) + 3); k++) load(k);
      kick();
    }

    // Images load ahead of the barrage (the next few always ready), decoded off the main thread
    function load(i) {
      const f = flyers[i];
      if (!f || f.loaded) return;
      f.loaded = true;
      f.img.sizes = f.src.sizes;
      f.img.srcset = f.src.srcset;
      f.img.src = f.src.getAttribute('src');
      if (f.img.decode) f.img.decode().catch(() => {});
    }

    // Crackling arcs round an AI certificate's edge, drawn to its size
    function arcs(f) {
      const old = f.card.querySelector('.mc-fly__arcs');
      if (old) old.remove();
      if (f.el.dataset.kind !== 'ai') return;
      const w = G.W + 28, h = f.h + 28, ns = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('class', 'mc-fly__arcs');
      svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      const edge = (x0, y0, x1, y1, n, amp, seed) => Array.from({ length: n + 1 }, (_, k) => {
        const t = k / n, j = k && k < n ? Math.sin((k + 1) * seed * 91.7) * amp : 0;
        const nx = -(y1 - y0), ny = x1 - x0, l = Math.hypot(nx, ny) || 1;
        return [lerp(x0, x1, t) + (nx / l) * j, lerp(y0, y1, t) + (ny / l) * j];
      });
      [[14, 14, w * 0.62, 14, 3.1], [w - 14, 14, w - 14, h * 0.7, 5.3], [w - 14, h - 14, w * 0.3, h - 14, 7.7], [14, h - 14, 14, h * 0.25, 2.9]].forEach(([a, b, c, d, sd]) => {
        const p = document.createElementNS(ns, 'path');
        p.setAttribute('d', edge(a, b, c, d, 9, 7, sd).map((q, k) => `${k ? 'L' : 'M'}${q[0].toFixed(1)} ${q[1].toFixed(1)}`).join(''));
        svg.append(p);
      });
      f.card.append(svg);
    }

    function layout() {
      const vw = document.documentElement.clientWidth, vh = innerHeight;
      const hd = document.querySelector('.mc-header');
      const header = (hd && hd.getBoundingClientRect().height) || 68;
      const wrap = section.querySelector('.mc-wrap');
      const g = wrap ? parseFloat(getComputedStyle(wrap).paddingLeft) || 16 : 16;
      const tops = segs.map((sg) => sg.getBoundingClientRect().top + scrollY);
      const last = segs[N - 1].getBoundingClientRect();
      const runEnd = last.bottom + scrollY;
      const hr = head ? head.getBoundingClientRect() : { top: 0, bottom: 0 };
      const headTop = hr.top + scrollY, headEnd = hr.bottom + scrollY;
      const wide = vw >= 990;
      const cap = 76;
      // the deck: beside the landing spot where there is room, else along the bottom
      const deckW = wide ? 150 : clamp(Math.round(vw * 0.24), 88, 140);
      let W = Math.min(vw - 2 * g, wide ? Math.min(720, vw * 0.58) : 760);
      const side = wide && (vw - W) / 2 >= deckW + 2 * g;
      const deckH = deckW / AR;
      const bottom = side ? vh - 16 : vh - 14 - deckH - 18;
      W = Math.min(W, (bottom - 12 - cap - header - 30) * AR);
      W = Math.max(160, Math.round(W / 2) * 2);
      const deck = side ? [vw - g - deckW / 2, vh * 0.55] : [vw / 2, vh - 14 - deckH / 2 - 8];
      const h = W / AR, top = header + 30, bot = bottom - 12 - cap;
      const centre = [vw / 2, bot - top >= h ? (top + bot) / 2 : bot - h / 2];
      const slots = items.map((li) => {
        const b = li.querySelector('.mc-cert__img').getBoundingClientRect();
        return [b.left + b.width / 2, b.top + scrollY + b.height / 2, b.width];
      });
      G = { vw, vh, header, tops, runEnd, headTop, headEnd, W, deckW, deck, centre, slots };
      flyers.forEach((f) => { f.h = h; f.el.style.setProperty('--w', W + 'px'); f.k = ''; arcs(f); });
      kick();
    }

    // Which certificate is up: the bottom of the screen against the stretches (-1 before the first, N for the wall).
    // Nothing launches until the count has landed (or the reader is already past the head).
    function target() {
      if (!G) return -1;
      const line = scrollY + innerHeight;
      if (line >= G.runEnd) return N;
      if (!ctl.done && scrollY + G.header < G.headEnd) return -1;
      // and never while the credentials are still low on a tall screen (the reader has not scrolled to them yet)
      if (G.headTop - scrollY > 0.5 * innerHeight) return -1;
      let k = -1;
      while (k + 1 < N && G.tops[k + 1] <= line) k++;
      return k;
    }
    const want = (i, t) => (t >= N ? 'wall' : i < t ? 'deck' : i === t ? 'centre' : 'away');

    // Poses: centre x, y, width, turn, opacity, projection
    function pose(f, st) {
      const i = f.i;
      if (st === 'centre') return { x: G.centre[0], y: G.centre[1], w: G.W, r: 0, op: 1, holo: 0 };
      if (st === 'deck') return { x: G.deck[0] + (i % 2 ? 2 : -2), y: G.deck[1] - i * 2, w: G.deckW, r: ((i * 37) % 7) - 3, op: 1, holo: 0 };
      if (st === 'wall') { const sl = G.slots[i]; return { x: sl[0], y: sl[1] - scrollY, w: sl[2], r: 0, op: 1, holo: 0 }; }
      // away: a point of light at its beacon in the sky
      const J = window.McJourney;
      const b = (J && J.beaconPx && J.beaconPx(Math.min(7, Math.floor((i * 8) / N)))) || ring(i);
      return { x: b[0], y: b[1], w: 8, r: (i % 2 ? 1 : -1) * 16, op: 0, holo: 1 };
    }
    function ring(i) {
      const a = -Math.PI / 2 + (i / N) * Math.PI * 2, R = Math.min(G.vw, G.vh) * 0.42;
      return [G.vw / 2 + Math.cos(a) * R, G.vh * 0.42 + Math.sin(a) * R];
    }

    // The legs a certificate takes from one state to another
    const ROUTES = {
      'away>centre': ['launch'], 'away>deck': ['skip'], 'away>wall': ['skip', 'explode'],
      'centre>deck': ['slam'], 'centre>away': ['retreat'], 'centre>wall': ['slam', 'explode'],
      'deck>centre': ['back'], 'deck>away': ['retreat'], 'deck>wall': ['explode'],
      'wall>deck': ['gather'], 'wall>centre': ['gather', 'back'], 'wall>away': ['gather', 'retreat'],
    };
    const LEG_TO = { launch: 'centre', skip: 'deck', slam: 'deck', retreat: 'away', back: 'centre', explode: 'wall', gather: 'deck' };

    function go(t, speed = 1) {
      const now = performance.now();
      pace = speed;
      // the deck explodes once everything bound for it has landed there
      const toWall = t >= N;
      let wait = 0;
      flyers.forEach((f) => {
        const from = f.leg ? LEG_TO[f.leg.kind] : f.st;
        const to = want(f.i, t);
        const route = from === to ? (f.leg ? [] : null) : ROUTES[`${from}>${to}`];
        if (!route) return;
        if (toWall && route[0] !== 'explode' && route.includes('explode')) wait = Math.max(wait, route[0] === 'slam' ? T.slam : T.skip);
        f.plan = route;
      });
      let blastAt = 0;
      flyers.forEach((f) => {
        if (!f.plan) return;
        const route = f.plan;
        f.plan = null;
        if (!route.length) { f.legs = []; return; } // already heading there: drop anything queued after it
        f.legs = route.map((kind) => ({ kind }));
        if (toWall) {
          const ex = f.legs.find((l) => l.kind === 'explode');
          if (ex) { ex.at = now + wait + f.i * T.stagger; blastAt = now + wait; }
        }
        startLeg(f, now);
      });
      if (blastAt) setTimeout(() => {
        if (cur < N) return;
        fx.go(G.deck[0], G.deck[1], Math.min(G.vw, G.vh) * 0.6, 'blast');
        kickCamera([layer, sky()], true);
      }, blastAt - now);
      if (!toWall) flyers.forEach((f) => toggle(f.li, 'is-placed', false));
      cur = t;
      for (let k = Math.max(0, t); k < Math.min(N, t + 4); k++) load(k);
    }

    function startLeg(f, now) {
      const l = f.legs.shift();
      if (!l) { f.leg = null; return; }
      const kind = l.kind;
      if (kind === 'launch') f.launchAt = Math.max(now, l.at || now);
      const dur = { launch: kinds[f.i] ? T.hero : T.launch * pace, skip: T.skip, slam: T.slam, retreat: T.retreat, back: T.back, explode: T.explode, gather: T.gather }[kind];
      if (kind === 'gather') toggle(f.li, 'is-placed', false);
      f.leg = { kind, from: f.pose || pose(f, f.st), t0: Math.max(now, l.at || now), dur, side: f.i % 2 ? 1 : -1 };
      f.st = LEG_TO[kind];
    }

    // Place a flyer with its centre at (x, y), w wide, turned r degrees
    function place(f, p) {
      const s = p.w / G.W;
      const k = s > 0.999 && Math.abs(p.r) < 0.01
        ? `translate(${Math.round(p.x - G.W / 2)}px,${Math.round(p.y - f.h / 2)}px)`
        : `translate(${p.x.toFixed(1)}px,${p.y.toFixed(1)}px) rotate(${p.r.toFixed(2)}deg) scale(${s.toFixed(4)}) translate(${-G.W / 2}px,${(-f.h / 2).toFixed(1)}px)`;
      if (k !== f.k) { f.el.style.transform = k; f.k = k; }
      const op = Math.round(p.op * 100) / 100, ho = Math.round(p.holo * 100) / 100;
      if (op !== f.op) { f.el.style.opacity = op; f.op = op; }
      if (ho !== f.ho) { f.holo.style.opacity = ho; f.ho = ho; }
    }
    const toggle = (el, c, on) => { if (el.classList.contains(c) !== on) el.classList.toggle(c, on); };
    function cls(f, c, on) { if (f.cls[c] !== on) { f.cls[c] = on; f.el.classList.toggle(c, on); } }

    function hit(f) {
      replay(f.el, 'is-hit', 1100);
      const kind = kinds[f.i] || 'hit';
      fx.go(G.centre[0], G.centre[1], G.W, kind);
      kickCamera([layer, sky()], kind !== 'hit');
    }

    // Where a leg has the certificate at time now
    function legPose(f, now) {
      const l = f.leg, a = l.from, b = pose(f, LEG_TO[l.kind]);
      const t = clamp((now - l.t0) / l.dur, 0, 1);
      switch (l.kind) {
        case 'launch': {
          // out of deep space: projected light that grows as it comes at you, then resolves into the certificate
          const e = easeOut(t);
          return { x: lerp(a.x, b.x, e) + Math.sin(Math.PI * e) * 30 * l.side, y: lerp(a.y, b.y, e), w: loglerp(a.w, b.w, e), r: lerp(a.r, 0, e),
            op: Math.max(a.op, Math.min(1, t * 5)), holo: Math.min(a.holo, 1 - smooth(0.55, 0.97, t)), t };
        }
        case 'slam': {
          const e = easeIn(t);
          return { x: lerp(a.x, b.x, e), y: lerp(a.y, b.y, e), w: loglerp(a.w, b.w, Math.pow(t, 0.8)), r: lerp(a.r, b.r, e), op: 1, holo: 0, t };
        }
        case 'explode': {
          // out of the deck on its own arc to its place in the wall
          const e = easeOut(t), arc = Math.sin(Math.PI * e);
          const dx = b.x - a.x, dy = b.y - a.y, dl = Math.hypot(dx, dy) || 1, out = arc * (40 + dl * 0.22), sw = ((f.i * 53) % 11) - 5;
          return { x: lerp(a.x, b.x, e) + (dx / dl) * out - (dy / dl) * sw * 6 * arc, y: lerp(a.y, b.y, e) + (dy / dl) * out + (dx / dl) * sw * 6 * arc,
            w: loglerp(a.w, b.w, e) * (1 + 0.22 * arc), r: lerp(a.r + sw * 9, 0, e), op: 1, holo: 0, t };
        }
        case 'retreat': {
          const e = easeIn(t);
          return { x: lerp(a.x, b.x, e), y: lerp(a.y, b.y, e), w: loglerp(a.w, Math.max(b.w, 8), e), r: lerp(a.r, b.r, e), op: 1 - smooth(0.6, 1, t), holo: smooth(0, 0.5, t), t };
        }
        default: { // skip, back, gather
          const e = l.kind === 'gather' ? easeIn(t) : easeOut(t);
          return { x: lerp(a.x, b.x, e), y: lerp(a.y, b.y, e), w: loglerp(a.w, b.w, e), r: lerp(a.r, b.r, e), op: l.kind === 'skip' ? Math.min(1, t * 4) : 1, holo: l.kind === 'skip' ? 1 - t : 0, t };
        }
      }
    }

    function frame() {
      raf = 0;
      if (!G) return;
      const t = target();
      if (!layer) {
        items.forEach((li) => toggle(li, 'is-placed', true));
        return;
      }
      const now0 = performance.now();
      if (t !== cur) {
        const c = flyers[cur];
        const fast = speed() > FAST, behind = t - cur;
        const holding = c && c.leg && c.leg.kind === 'launch' && !c.leg.hit;
        const heroHold = c && kinds[cur] && now0 - (c.launchAt || 0) < HOLD;
        if (t < cur || (fast && behind > SKIP) || behind > 3 * SKIP) go(t);
        else if (!holding && !heroHold) go(cur + 1, PACE[Math.min(behind, PACE.length - 1)]);
      }
      const now = performance.now();
      let busy = false, anyVis = false;
      flyers.forEach((f) => {
        let p, moving = false;
        if (f.leg) {
          if (now < f.leg.t0) { p = f.leg.from; moving = true; }
          else {
            p = legPose(f, now);
            moving = true;
            // a launch has landed (and hits) at three quarters of its time: the ease has it all but there
            if (f.leg.kind === 'launch' && p.t >= 0.75 && !f.leg.hit) { f.leg.hit = true; hit(f); }
            if (p.t >= 1) {
              const done = f.leg.kind;
              f.leg = null;
              f.pose = p;
              if (done === 'explode') toggle(f.li, 'is-placed', true);
              startLeg(f, now);
            }
          }
          busy = true;
        } else p = f.st === 'away' || f.st === 'wall' ? null : pose(f, f.st);
        if (f.st === 'wall' && !f.leg) toggle(f.li, 'is-placed', true);
        const vis = !!p && !(f.st === 'away' && !moving);
        if (p) { f.pose = p; place(f, p); } else f.pose = null;
        anyVis = anyVis || vis;
        cls(f, 'is-vis', vis);
        const held = f.st === 'centre' && !f.leg;
        cls(f, 'is-held', held);
        cls(f, 'is-landed', (held || (f.st === 'deck' && !f.leg) || (f.leg && f.leg.kind === 'slam')) && cur < N);
      });

      // the page behind dims while the barrage runs; the beacons light as certificates land
      const dim = cur >= 0 && cur < N;
      if (wasDim && !dim) dimOff = now + 360; // keep the layer up while the dimming fades out
      wasDim = dim;
      toggle(layer, 'is-dim', dim);
      const show = anyVis || dim || now < dimOff;
      if (layer.hidden === show) layer.hidden = !show;
      const landed = clamp(cur + (flyers[cur] && flyers[cur].leg ? 0 : 1), 0, N);
      marks.forEach((m, k) => toggle(m, 'is-on', landed >= Math.ceil(((k + 1) * N) / marks.length)));
      if (busy || now < dimOff || t !== cur) kick(); // (also keeps a hero's hold ticking); idle otherwise
    }

    const kick = () => { if (!raf) raf = requestAnimationFrame(frame); };
    // Scroll speed in screens a second (smoothed; it falls away once the scroll stops)
    let vY = scrollY, vT = performance.now(), vS = 0;
    const speed = () => (performance.now() - vT > 220 ? 0 : vS);
    addEventListener('scroll', () => {
      const now = performance.now(), dt = Math.max(8, now - vT);
      const v = (Math.abs(scrollY - vY) / dt) * 1000 / innerHeight;
      vS = now - vT > 220 ? v : vS * 0.7 + v * 0.3;
      vY = scrollY; vT = now;
      kick();
    }, { passive: true });

    // Any resize re-measures (debounced)
    let rt = 0;
    const relayout = () => { clearTimeout(rt); rt = setTimeout(layout, 120); };
    addEventListener('resize', relayout);
    new ResizeObserver(relayout).observe(section);
    document.fonts && document.fonts.ready.then(relayout);
    layout();

    // Build the flight layer once the section is within a couple of screens, and never before the page has finished
    // loading: on the first scroll, or when the browser is idle. The first certificates' images load then.
    const settled = new Promise((res) => {
      addEventListener('scroll', res, { once: true, passive: true });
      const idle = () => (window.requestIdleCallback ? requestIdleCallback(res, { timeout: 3000 }) : setTimeout(res, 1500));
      if (document.readyState === 'complete') idle();
      else addEventListener('load', idle, { once: true });
    });
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) { io.disconnect(); settled.then(build); }
    }, { rootMargin: '200% 0px' });
    io.observe(section);
  }

  function init() {
    document.querySelectorAll('[data-mc-certs]').forEach((section) => {
      if (section.dataset.mcCertsReady) return;
      section.dataset.mcCertsReady = '1';
      viewer(section);
      try {
        if (section.classList.contains('is-armed') && 'IntersectionObserver' in window && 'ResizeObserver' in window) sequence(section);
        else {
          section.classList.remove('is-armed');
          if (section.classList.contains('is-tally-armed')) tally(section, () => {});
        }
      } catch (err) {
        // the finished page (head and wall) whatever happens
        section.classList.remove('is-armed', 'is-tally-armed');
        throw err;
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
